import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Alert,
  AlertTitle,
  CircularProgress,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  FormControlLabel,
  Stack
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  TravelExplore as TravelExploreIcon,
  RocketLaunch as RocketLaunchIcon,
  EmojiEvents as EmojiEventsIcon,
  Link as LinkIcon,
  VpnKey as VpnKeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Api as ApiIcon,
  Add as AddIcon,
  ShieldOutlined as ShieldOutlinedIcon
} from '@mui/icons-material';
import { useEffect } from 'react';
import { alpha , darken, lighten,styled} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import { notifyCreditsChanged } from '../../services/creditsApi';
import { AddCircle as AddCircleIcon, Delete as DeleteIcon } from '@mui/icons-material';


/* --------- Styled to match External Pentest --------- */

const HeaderCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: 16,
  background: `linear-gradient(135deg, ${PRIMARYCOLOR} 0%, ${darken(PRIMARYCOLOR, 0.5)} 100%)`,
  color: '#fff',
  boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
}));

const SectionCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  borderRadius: 16,
  boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
}));

const StepIconContainer = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active
    ? theme.palette.primary.main
    : ownerState.completed
    ? theme.palette.success.main
    : theme.palette.grey[300],
  color: '#fff',
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: ownerState.active ? '0 0 0 6px rgba(25,118,210,0.15)' : 'none'
}));

function CustomStepIcon(props) {
  const { active, completed, icon } = props;
  const mapping = {
    1: <AssignmentIcon fontSize="small" />,  // Project Name
    2: <LinkIcon fontSize="small" />,        // Login URL
    3: <VpnKeyIcon fontSize="small" />,      // Credentials
    4: <RocketLaunchIcon fontSize="small" />,// Deploy
    5: <EmojiEventsIcon fontSize="small" />  // Done
  };
  return (
    <StepIconContainer ownerState={{ active, completed }}>
      {mapping[icon] || <AssignmentIcon fontSize="small" />}
    </StepIconContainer>
  );
}

const steps = ['Project Name', 'Login URL', 'Credentials', 'Deploy Agent', 'Done'];

const splitHostnameEntries = (input) => String(input || '')
  .split(/[,\r\n]+/)
  .map((entry) => entry.trim())
  .filter(Boolean);

const normalizeHostname = (input) => {
  const rawValue = String(input || '').trim();
  if (!rawValue) return { hostname: '' };
  if (rawValue.includes('*')) {
    return { error: `Wildcards are not supported: ${rawValue}` };
  }

  try {
    const hasProtocol = /^[a-z][a-z\d+.-]*:\/\//i.test(rawValue);
    const candidate = hasProtocol ? rawValue : `https://${rawValue.replace(/^\/\//, '')}`;
    const parsed = new URL(candidate);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return { error: `Only HTTP and HTTPS hostnames are supported: ${rawValue}` };
    }

    const hostname = parsed.hostname.toLowerCase().replace(/\.$/, '');
    const labels = hostname.split('.');
    const validLabels = labels.every((label) => (
      label.length > 0
      && label.length <= 63
      && /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label)
    ));

    if (!hostname || hostname.length > 253 || !validLabels) {
      return { error: `Could not identify a valid hostname from: ${rawValue}` };
    }

    return { hostname };
  } catch {
    return { error: `Could not identify a valid hostname from: ${rawValue}` };
  }
};

const mergeHostnameEntries = (input, currentValues) => {
  const entries = splitHostnameEntries(input);
  const accepted = [];
  const rejected = [];

  entries.forEach((entry) => {
    const result = normalizeHostname(entry);
    if (result.error) rejected.push(entry);
    else if (result.hostname) accepted.push(result.hostname);
  });

  return {
    values: Array.from(new Set([...(currentValues || []), ...accepted])),
    rejected,
  };
};

const HostnameChipInput = ({
  title,
  badge,
  description,
  accent,
  Icon,
  values,
  draft,
  error,
  onDraftChange,
  onCommit,
  onRemove,
}) => {
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      onCommit(draft);
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData?.getData('text') || '';
    if (!/[,\r\n]/.test(pasted)) return;
    event.preventDefault();
    onCommit([draft, pasted].filter(Boolean).join(','));
  };

  return (
    <Box
      sx={{
        height: '100%',
        p: 2,
        borderRadius: 3,
        border: `1px solid ${alpha(accent, 0.18)}`,
        bgcolor: alpha(accent, 0.035),
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <Box sx={{ width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: alpha(accent, 0.1), color: accent }}>
            <Icon fontSize="small" />
          </Box>
          <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
        </Stack>
        <Chip size="small" label={badge} sx={{ bgcolor: alpha(accent, 0.1), color: accent, fontWeight: 800 }} />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40, mb: 1.5, lineHeight: 1.55 }}>
        {description}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          label="Hostname or URL"
          placeholder="api.example.com"
          value={draft}
          error={Boolean(error)}
          helperText={error || 'URLs are accepted; only the hostname will be saved.'}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={!draft.trim()}
          onClick={() => onCommit(draft)}
          sx={{ minHeight: 40, flexShrink: 0, borderRadius: 2.5, fontWeight: 800 }}
        >
          Add
        </Button>
      </Stack>

      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5, minHeight: 28 }}>
        {values.length ? values.map((hostname) => (
          <Chip
            key={hostname}
            label={hostname}
            onDelete={() => onRemove(hostname)}
            size="small"
            sx={{ bgcolor: '#fff', border: `1px solid ${alpha(accent, 0.2)}`, color: '#0f172a', fontWeight: 700 }}
          />
        )) : (
          <Typography variant="caption" color="text.secondary">No additional hostnames added.</Typography>
        )}
      </Stack>
    </Box>
  );
};

/* --------- Component --------- */

const CreateWebPentest = () => {
  const navigate = useNavigate();

  // wizard state
  const [activeStep, setActiveStep] = useState(0);

  // form inputs
  const [projectName, setProjectName]   = useState('');
  const [organizationId, setOrganizationId] = useState(-1);
  const [organizations, setOrganizations] = useState([{ id: -1, name: 'None' }]);
  const [loginUrl, setLoginUrl]         = useState('');
  const [apiAllowedDomains, setApiAllowedDomains] = useState([]);
  const [authAllowedDomains, setAuthAllowedDomains] = useState([]);
  const [apiHostnameDraft, setApiHostnameDraft] = useState('');
  const [authHostnameDraft, setAuthHostnameDraft] = useState('');
  const [apiHostnameError, setApiHostnameError] = useState('');
  const [authHostnameError, setAuthHostnameError] = useState('');
  const [username, setUsername]         = useState('');
  const [password, setPassword]         = useState('');
  const [loginInstructions, setLoginInstructions] = useState('');
  const [cookieForm, setCookieForm] = useState({ name: '', value: '', domain: '', path: '' });
  const [cookies, setCookies] = useState([]);


  const [useLoginPage, setUseLoginPage] = useState(true);
  const [useCookies, setUseCookies] = useState(false);

  // UX helpers
  const [urlError, setUrlError]         = useState(false);
  const [isDeploying, setIsDeploying]   = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const prependNone = (list = []) => {
  const cleaned = (list || []).filter(o => o && Number(o.id) !== -1);
  return [{ id: -1, name: 'None' }, ...cleaned];
};

function loadOrganizationsBasic() {
  const token = sessionStorage.getItem('token');
  if (!token) {
    try { navigate('/'); } catch { /* optional if navigate not available */ }
    return;
  }
  fetch(`${BASEURL}/organizations?basic=1`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
    .then(r => r.json())
    .then(data => {
      if (data?.error) {
        console.error(data.message || 'Error fetching organizations (basic)');
        setOrganizations([{ id: -1, name: 'None' }]);
        setOrganizationId(-1);
        return;
      }
      const list = Array.isArray(data?.organizations) ? data.organizations : [];
      setOrganizations(prependNone(list));
      setOrganizationId(-1); // default selection
    })
    .catch(err => {
      console.error('Failed to fetch organizations (basic):', err);
      setOrganizations([{ id: -1, name: 'None' }]);
      setOrganizationId(-1);
    });
}

// call once on mount
useEffect(() => {
  loadOrganizationsBasic();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  /* ---------- helpers ---------- */

  const validateUrl = (url) => {
    const valid = /^https?:\/\/.+/i.test(url.trim());
    setUrlError(!valid);
    return valid;
  };

  const commitApiHostnames = (input = apiHostnameDraft) => {
    const result = mergeHostnameEntries(input, apiAllowedDomains);
    setApiAllowedDomains(result.values);
    setApiHostnameDraft(result.rejected.join(', '));
    setApiHostnameError(result.rejected.length ? 'Remove wildcards or enter a valid hostname or URL.' : '');
    return result.rejected.length === 0;
  };

  const commitAuthHostnames = (input = authHostnameDraft) => {
    const result = mergeHostnameEntries(input, authAllowedDomains);
    setAuthAllowedDomains(result.values);
    setAuthHostnameDraft(result.rejected.join(', '));
    setAuthHostnameError(result.rejected.length ? 'Remove wildcards or enter a valid hostname or URL.' : '');
    return result.rejected.length === 0;
  };

  const handleAccessNext = () => {
    const loginUrlValid = validateUrl(loginUrl);
    const apiHostnamesValid = commitApiHostnames();
    const authHostnamesValid = commitAuthHostnames();
    if (loginUrlValid && apiHostnamesValid && authHostnamesValid) setActiveStep(2);
  };

  /* ---------- keep this EXACT (API payload & flow) ---------- */
  const handleSaveAndDeploy = () => {
    if (!projectName || !validateUrl(loginUrl)) return;

    setIsDeploying(true);
    const token = sessionStorage.getItem('token');

    fetch(`${BASEURL}/pentests/web`, {                
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        name: projectName,
        scope: {
          login_url: loginUrl.trim(),
          api_allowed_domains: apiAllowedDomains,
          auth_allowed_domains: authAllowedDomains,
          credentials: [{
            username,
            password,
            ...(loginInstructions.trim() ? { login_instructions: loginInstructions } : {})
          }],
          cookies: cookies,          
        },
        organizationId: organizationId,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to create Web pentest');
        return res.json();
      })
      .then((data) => {
        notifyCreditsChanged({ reason: 'pentest-created', pentestId: data?.pentestId });
        setIsDeploying(false);
        setActiveStep(4);
      })
      .catch((err) => {
        console.error(err);
        setIsDeploying(false);
      });
  };
  /* ---------- /keep ---------- */

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexGrow: 1, width: '100%', p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <HeaderCard>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Create Web Pentest Project
            </Typography>
            <Typography sx={{ opacity: 0.9 }}>
              Configure your web-app test in minutes. Provide the login URL and a test account to get started.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Chip
              icon={<TravelExploreIcon />}
              label="AI Agents"
              variant="outlined"
              sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" }, mr: 1 }}
            />
            <Chip
              icon={<RocketLaunchIcon />}
              label="Fast setup"
              variant="outlined"
              sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" } }}
            />
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, idx) => (
                <Step key={idx}>
                  <StepLabel StepIconComponent={CustomStepIcon} sx={{ color: '#fff' }}>
                    <Typography sx={{ color: '#fff' }}>{label}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
        </Grid>
      </HeaderCard>

      {/* Step 0 – Project Name */}
      {activeStep === 0 && (
        <SectionCard sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Project Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Give your web pentest a clear, searchable name.
              </Typography>
              <TextField
                fullWidth
                label="Project Name"
                placeholder="e.g., Acme Web Pentest – Q3"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth>
                <InputLabel id="org-select-label">Organization</InputLabel>
                <Select
                labelId="org-select-label"
                label="Organization"
                value={organizationId}
                onChange={(e) => setOrganizationId(Number(e.target.value))}
                >
                {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                    {org.name}
                    </MenuItem>
                ))}
                </Select>
            </FormControl>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(1)}
                  disabled={!projectName.trim()}
                >
                  Next
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                sx={{
                  width: '100%',
                  minHeight: 220,
                  borderRadius: 3,
                  p: 3,
                  bgcolor: 'rgba(0, 92, 197, 0.06)',
                  border: '1px solid rgba(0, 92, 197, 0.20)'
                }}
              >
                <Typography fontWeight={800} sx={{ mb: 1, color: '#005CC5' }}>
                  ASSESSMENT GUIDANCE
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Web application assessments can take several hours to complete, depending on the size and complexity of the application. We recommend running the agent against a development or staging environment instead of production to avoid disrupting live users and workflows.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {/* Step 1 – Login URL */}
      {activeStep === 1 && (
        <SectionCard sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Login URL
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter the full URL of your web application’s login page.
          </Typography>

          <TextField
            fullWidth
            size="small"
            label="Login URL (e.g., https://example.com/login)"
            value={loginUrl}
            error={urlError}
            helperText={urlError ? 'Must start with http:// or https://' : null}
            onChange={(e) => {
              setLoginUrl(e.target.value);
              if (urlError) validateUrl(e.target.value);
            }}
            onBlur={(e) => validateUrl(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 44, alignItems: 'center' },
              '& .MuiInputBase-input': { py: 0 }
            }}
          />

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Additional Traffic Hosts</Typography>
            <Typography variant="body2" color="text.secondary">
              Optionally include API traffic observed during the assessment or allow external identity providers during authentication.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <HostnameChipInput
                title="API Hostnames"
                badge="Test Target"
                description="Captured HTTP requests to these exact hosts may be tested. The agent will not crawl them."
                accent="#005CC5"
                Icon={ApiIcon}
                values={apiAllowedDomains}
                draft={apiHostnameDraft}
                error={apiHostnameError}
                onDraftChange={(value) => {
                  setApiHostnameDraft(value);
                  if (apiHostnameError) setApiHostnameError('');
                }}
                onCommit={commitApiHostnames}
                onRemove={(hostname) => setApiAllowedDomains((current) => current.filter((value) => value !== hostname))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <HostnameChipInput
                title="SSO / Authentication Hostnames"
                badge="Login Only"
                description="These hosts may be visited during SSO or login, but they will not be treated as pentest targets."
                accent="#0f766e"
                Icon={ShieldOutlinedIcon}
                values={authAllowedDomains}
                draft={authHostnameDraft}
                error={authHostnameError}
                onDraftChange={(value) => {
                  setAuthHostnameDraft(value);
                  if (authHostnameError) setAuthHostnameError('');
                }}
                onCommit={commitAuthHostnames}
                onRemove={(hostname) => setAuthAllowedDomains((current) => current.filter((value) => value !== hostname))}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={() => setActiveStep(0)}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleAccessNext}
              disabled={!loginUrl.trim()}
            >
              Next
            </Button>
          </Box>
        </SectionCard>
      )}

      {/* Step 2 – Credentials */}
{activeStep === 2 && (
  <SectionCard sx={{ p: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
      Test Account Credentials
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Provide a standard user account for assessment (avoid admin if possible).
    </Typography>

    {/* Toggle for Login Page */}
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Typography variant="body1" sx={{ fontWeight: 600, mr: 2 }}>
        Use Login Page
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={useLoginPage}
            onChange={(e) => {
              const checked = e.target.checked;
              setUseLoginPage(checked);
              if (!checked) {
                setUsername('');
                setPassword('');
                setLoginInstructions('');
              }
            }}
            color="primary"
          />
        }
        label={useLoginPage ? 'On' : 'Off'}
      />
    </Box>

    {/* Username/Password (only if useLoginPage is ON) */}
    {useLoginPage && (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyIcon />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': { height: 44, alignItems: 'center' },
                '& .MuiInputBase-input': { py: 0 }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showPassword ? 'Hide' : 'Show'}>
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((s) => !s)}
                        size="small"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': { height: 44, alignItems: 'center' },
                '& .MuiInputBase-input': { py: 0 }
              }}
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Login Instructions (Optional)"
          value={loginInstructions}
          onChange={(e) => setLoginInstructions(e.target.value)}
          placeholder="Enter organization IDs, static PINs, account selection, MFA handling, or unusual login steps."
          helperText="Include any additional steps the assessment agent needs to authenticate successfully."
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 2 }}
        />
      </>
    )}

    {/* --- Cookies Section --- */}
    <Divider sx={{ my: 3 }} />

    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Typography variant="body1" sx={{ fontWeight: 600, mr: 2 }}>
        Use Cookies
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={useCookies}
            onChange={(e) => setUseCookies(e.target.checked)}
            color="primary"
          />
        }
        label={useCookies ? 'On' : 'Off'}
      />
    </Box>

    {useCookies && (
      <>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Cookies
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add any cookies you want the agent to include when logging in.
        </Typography>

        {/* Cookie input row */}
        <Grid container spacing={1} alignItems="center">
          {['name', 'value', 'domain', 'path'].map((field) => (
            <Grid item xs={12} sm={6} md={2.5} key={field}>
              <TextField
                fullWidth
                size="small"
                label={field.charAt(0).toUpperCase() + field.slice(1)}
                value={cookieForm?.[field] || ''}
                onChange={(e) =>
                  setCookieForm((prev) => ({ ...prev, [field]: e.target.value }))
                }
              />
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={1.5}>
            <IconButton
              color="primary"
              sx={{ mt: 1 }}
              onClick={() => {
                if (
                  cookieForm.name &&
                  cookieForm.value &&
                  cookieForm.domain &&
                  cookieForm.path
                ) {
                  setCookies((prev) => [...prev, cookieForm]);
                  setCookieForm({ name: '', value: '', domain: '', path: '' });
                }
              }}
            >
              <AddCircleIcon />
            </IconButton>
          </Grid>
        </Grid>

        {/* Cookies table */}
        {cookies.length > 0 && (
          <Box sx={{ mt: 2, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Name', 'Value', 'Domain', 'Path', 'Actions'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '6px 8px',
                        borderBottom: '1px solid #ddd'
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cookies.map((c, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '6px 8px' }}>{c.name}</td>
                    <td style={{ padding: '6px 8px' }}>{c.value}</td>
                    <td style={{ padding: '6px 8px' }}>{c.domain}</td>
                    <td style={{ padding: '6px 8px' }}>{c.path}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <IconButton
                        color="error"
                        onClick={() =>
                          setCookies((prev) => prev.filter((_, i) => i !== idx))
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </>
    )}

    <Divider sx={{ my: 3 }} />

    <Box sx={{ display: 'flex', gap: 2 }}>
      <Button variant="outlined" onClick={() => setActiveStep(1)}>
        Back
      </Button>
      <Button
        variant="contained"
        onClick={() => setActiveStep(3)}
        disabled={
          useLoginPage && (!username.trim() || !password.trim())
        }
      >
        Next
      </Button>
    </Box>
  </SectionCard>
)}




      {/* Step 3 – Deploy Agent */}
      {activeStep === 3 && (
        <SectionCard sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Agent Deployment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We’ll deploy your web testing agent to the cloud.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 200 }}>
            {isDeploying ? (
              <>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Deploying…</Typography>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 700,
                    mb: 2,
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid rgba(0, 92, 197, 0.16)',
                    bgcolor: 'rgba(0, 92, 197, 0.04)',
                  }}
                >
                  <Typography sx={{ fontWeight: 850, mb: 1.5 }}>Scope Review</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Login URL
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.35, mb: 1.5, fontWeight: 700, overflowWrap: 'anywhere' }}>
                    {loginUrl}
                  </Typography>

                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    API test targets
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.6, mb: 1.5 }}>
                    {apiAllowedDomains.length ? apiAllowedDomains.map((hostname) => (
                      <Chip key={hostname} label={hostname} size="small" sx={{ bgcolor: '#fff', fontWeight: 700 }} />
                    )) : <Typography variant="body2" color="text.secondary">None added</Typography>}
                  </Stack>

                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Authentication-only hosts
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
                    {authAllowedDomains.length ? authAllowedDomains.map((hostname) => (
                      <Chip key={hostname} label={hostname} size="small" sx={{ bgcolor: '#fff', fontWeight: 700 }} />
                    )) : <Typography variant="body2" color="text.secondary">None added</Typography>}
                  </Stack>
                </Box>
                <Alert severity="info" sx={{ maxWidth: 700, textAlign: 'left', mb: 3 }}>
                  <AlertTitle>Auto-Deployment</AlertTitle>
                  We’ll containerize your configuration and kick off the web-app pentest automatically. This can take a few minutes.
                </Alert>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={() => setActiveStep(2)}>
                    Back
                  </Button>
                  <Button variant="contained" onClick={handleSaveAndDeploy}>
                    Deploy
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </SectionCard>
      )}

      {/* Step 4 – Done */}
      {activeStep === 4 && (
        <SectionCard sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 100%)' }}>
          <EmojiEventsIcon sx={{ fontSize: 56, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
            You’re all set! 🎉
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            Your web-application pentest has been created. You can manage it from the dashboard.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button variant="contained" onClick={() => navigate('/pentests/')}>
              Finish
            </Button>
          </Box>
        </SectionCard>
      )}
    </Box>
  );
};

export default CreateWebPentest;
